import { create } from "zustand";

import type { ContactRow, DocumentRow, SupplierFields } from "../types";

interface FormState {
  step: number;
  supplier: SupplierFields;
  /**
   * Snapshot of the supplier as it arrived from prefill. Captured once on
   * hydrate and never mutated by user edits — Step 5 diffs the live
   * `supplier` against this to surface field-level changes.
   */
  originalSupplier: SupplierFields;
  /**
   * Frozen prefill snapshot of contacts. Used by Step 5 to detect changes
   * that aren't visible via individual rows' action flags — most notably
   * primary-contact toggling, where no contact's own data changes.
   */
  originalContacts: ContactRow[];
  contacts: ContactRow[];
  documents: DocumentRow[];
  /**
   * True after the first prefill response has completed (success OR error).
   * Used by TopNav so the editing-context strip doesn't flash a misleading
   * "New supplier registration" label during the initial Dataverse load.
   */
  hydrated: boolean;

  setStep: (step: number) => void;
  setSupplier: (patch: Partial<SupplierFields>) => void;
  setContacts: (rows: ContactRow[]) => void;
  setDocuments: (rows: DocumentRow[]) => void;
  /** Initialize from a prefill response. Sets both `supplier` (editable) and
   * `originalSupplier` (frozen diff baseline) from the same record. */
  hydrate: (data: {
    supplier?: SupplierFields | null;
    contacts: ContactRow[];
    documents: DocumentRow[];
  }) => void;
  reset: () => void;
}

const initialSupplier: SupplierFields = { id: null };

export const useFormStore = create<FormState>((set) => ({
  step: 1,
  supplier: initialSupplier,
  originalSupplier: initialSupplier,
  originalContacts: [],
  contacts: [],
  documents: [],
  hydrated: false,

  setStep: (step) => set({ step }),
  setSupplier: (patch) => set((s) => ({ supplier: { ...s.supplier, ...patch } })),
  setContacts: (contacts) => set({ contacts }),
  setDocuments: (documents) => set({ documents }),
  hydrate: ({ supplier, contacts, documents }) =>
    set({
      supplier: supplier ?? initialSupplier,
      originalSupplier: supplier ?? initialSupplier,
      // Structured-clone the contacts so mutations on `contacts` (e.g. the
      // primary-radio handler) can't leak back into `originalContacts`.
      originalContacts: contacts.map((c) => ({ ...c })),
      contacts,
      documents,
      hydrated: true,
    }),
  reset: () =>
    set({
      step: 1,
      supplier: initialSupplier,
      originalSupplier: initialSupplier,
      originalContacts: [],
      contacts: [],
      documents: [],
      hydrated: false,
    }),
}));
